import { inject } from '@angular/core';
import type { CanDeactivateFn, Route, Routes } from '@angular/router';
import type { AuthPermission } from './core/api/generated/models/AuthPermission';
import { acceptInviteGuard, authGuard, guestOnlyGuard } from './core/auth/auth.guard';
import { anyPermissionGuard, permissionGuard } from './core/auth/rbac.guard';
import { billingPaywallGuard } from './features/billing/billing-paywall.guard';
import { PLACEHOLDER_ROUTE_MODULES, type AppModuleNavItem } from './core/navigation/app-modules';
import { AgentBuilderPage } from './features/agents/builder/agent-builder.page';
import { AgentDetailPage } from './features/agents/detail/agent-detail.page';
import { AutomationBuilderPage } from './features/agents/automations/automation-builder.page';
import { AgentsListPage } from './features/agents/list/agents-list.page';
import { ArchetypeEditorPage } from './features/agents/archetypes/archetype-editor.page';
import { ArchetypesListPage } from './features/agents/archetypes/archetypes-list.page';
import { ConversationPreferencesPage } from './features/conversations/pages/conversation-preferences.page';
import { ConversationsPage } from './features/conversations/pages/conversations.page';
import { LabelsSettingsPage } from './features/conversations/pages/labels-settings.page';
import { SlaSettingsPage } from './features/conversations/pages/sla-settings.page';
import { Dashboard } from './features/dashboard/dashboard';
import { ForbiddenPage } from './features/forbidden/forbidden.page';
import { KnowledgePage } from './features/knowledge/knowledge.page';
import { LoginPage } from './features/login/login.page';
import { ModulePlaceholderPage } from './features/module-placeholder/module-placeholder.page';
import { BusinessOnboardingPage } from './features/onboarding/business-onboarding.page';
import { RevenueCatalogPage } from './features/revenue/catalog/revenue-catalog.page';
import { ProdutoDetalhePage } from './features/revenue/catalog/produto-detalhe.page';
import { VendasListaPage } from './features/revenue/sales/sales-lista.page';
import { SalesCriarPage } from './features/revenue/sales/sales-criar.page';
import { SalesEditarPage } from './features/revenue/sales/sales-editar.page';
import { LeadsListaPage } from './features/revenue/leads/leads-lista.page';
import { ImportWizardPage } from './features/revenue/imports/import-wizard.page';
import { WhatsappSignupTestPage } from './features/whatsapp-signup-test/whatsapp-signup-test.page';
import { SettingsPage } from './features/settings/settings.page';
import { GeneralSettingsPage } from './features/settings/pages/general-settings.page';
import { ProfileSettingsPage } from './features/settings/pages/profile-settings.page';
import { SecuritySettingsPage } from './features/settings/pages/security-settings.page';
import { SubscriptionSettingsPage } from './features/settings/pages/subscription-settings.page';
import { TeamsSettingsPage } from './features/settings/pages/teams-settings.page';
import { UsersSettingsPage } from './features/settings/pages/users-settings.page';
import { AppShellComponent } from './layouts/app-shell/app-shell';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout';
import { UiConfirmDialogService } from './shared/ui/ui-confirm-dialog.service';

const CONVERSATION_VISIBILITY_PERMISSIONS = [
  'conversations:base:visibility:all',
  'conversations:base:visibility:team',
  'conversations:base:visibility:own',
] satisfies readonly AuthPermission[];

const USERS_SETTINGS_PERMISSIONS = [
  'core:roles:read',
  'core:memberships:read',
  'core:memberships:invite',
  'core:teams:read',
] satisfies readonly AuthPermission[];

const TEAMS_SETTINGS_PERMISSIONS = [
  'core:teams:read',
  'core:memberships:read',
  'core:roles:read',
] satisfies readonly AuthPermission[];

const SECURITY_SETTINGS_PERMISSIONS = [
  'core:roles:read',
  'core:memberships:read',
  'core:teams:read',
  'core:service-accounts:read',
  'core:api-keys:read',
] satisfies readonly AuthPermission[];

export const agentBuilderCanDeactivate: CanDeactivateFn<AgentBuilderPage> = (
  component,
  _currentRoute,
  _currentState,
  nextState,
) => {
  if (!component.store.dirty()) return true;
  const agentId = component.store.agentId();
  if (agentId && nextState?.url.startsWith(`/agentes/${agentId}`)) return true;
  return inject(UiConfirmDialogService).confirm({
    title: 'Sair sem salvar?',
    description: 'Existem alterações locais no construtor. Se sair agora, elas serão descartadas.',
    confirmLabel: 'Sair sem salvar',
    cancelLabel: 'Continuar editando',
    tone: 'warning',
  });
};

export const archetypeEditorCanDeactivate: CanDeactivateFn<ArchetypeEditorPage> = (component) => {
  if (!component.dirty()) return true;
  return inject(UiConfirmDialogService).confirm({
    title: 'Sair sem salvar?',
    description: 'As alterações locais no fluxo do arquétipo serão descartadas.',
    confirmLabel: 'Sair sem salvar',
    cancelLabel: 'Continuar editando',
    tone: 'warning',
  });
};

export const routes: Routes = [
  {
    path: 'design-system',
    component: Dashboard,
  },
  {
    path: 'whatsapp-signup-test',
    component: WhatsappSignupTestPage,
  },
  {
    path: 'integracoes/instagram/callback',
    loadComponent: () =>
      import('./features/integracoes/pages/instagram-callback.page').then((m) => m.InstagramCallbackPage),
  },
  {
    // Generic OAuth2 redirect landing (Google Calendar, and future oauth-redirect providers).
    path: 'integracoes/oauth/callback',
    loadComponent: () =>
      import('./features/integracoes/pages/oauth-callback.page').then((m) => m.OAuthCallbackPage),
  },
  {
    path: 'login',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        canActivate: [guestOnlyGuard],
        data: {
          mode: 'login',
        },
        component: LoginPage,
      },
    ],
  },
  {
    path: 'signup',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        canActivate: [guestOnlyGuard],
        data: {
          mode: 'signup',
        },
        component: LoginPage,
      },
    ],
  },
  {
    path: 'forgot-password',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        canActivate: [guestOnlyGuard],
        data: {
          mode: 'forgot-password',
        },
        component: LoginPage,
      },
    ],
  },
  {
    path: 'reset-password',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        canActivate: [guestOnlyGuard],
        data: {
          mode: 'reset-password',
        },
        component: LoginPage,
      },
    ],
  },
  {
    // Unified "código por email": post-signup confirmation AND "entrar por código"
    // (passwordless login) share this one route/screen — see internal-auth.service.ts on the
    // API side. Previously the passwordless-login case had NO route at all (pure runtime state
    // on top of /login), so refresh/deep-link/back all broke — that's the bug behind "entrar via
    // código não funciona, sem botão voltar".
    path: 'email-code',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        canActivate: [guestOnlyGuard],
        data: {
          mode: 'email-code',
        },
        component: LoginPage,
      },
    ],
  },
  {
    // Descadastro público (link do rodapé do e-mail de disparo). Totalmente público — sem guard;
    // a autenticidade vem do token assinado. Usa o auth-layout pra manter a identidade Automato.
    path: 'descadastrar',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        loadComponent: () => import('./features/descadastro/unsubscribe.page').then((m) => m.UnsubscribePage),
      },
    ],
  },
  {
    // Landing page for an invitation email link. Unlike the routes above, this one must work
    // both anonymous AND already-authenticated (an existing user invited to a second business),
    // so it uses acceptInviteGuard instead of guestOnlyGuard — see that guard's doc comment.
    path: 'aceitar-convite',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        canActivate: [acceptInviteGuard],
        data: {
          mode: 'accept-invite',
        },
        component: LoginPage,
      },
    ],
  },
  {
    path: 'onboarding',
    canActivate: [authGuard],
    component: BusinessOnboardingPage,
  },
  {
    path: '',
    canActivate: [authGuard],
    // D1/W1-2: assinatura expired/suspended/cancelled trava TODA navegação dentro do shell, exceto
    // a própria tela de planos e o Perfil (ver PAYWALL_ALLOWLIST em billing-paywall.guard.ts).
    canActivateChild: [billingPaywallGuard],
    component: AppShellComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
      },
      {
        path: 'dashboard',
        // Landing default de todo usuário autenticado (redirectTo acima) — sem permissionGuard
        // aqui: o banner de trial e o restante da página são pra todo mundo. O painel executivo
        // (KPIs de SLA/pipeline/receita) é gated DENTRO do componente por analytics:dashboard:read
        // (só owner/admin/manager), não bloqueando a rota inteira.
        loadComponent: () => import('./features/home/home.page').then((m) => m.HomePage),
      },
      {
        path: 'configuracoes',
        component: SettingsPage,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'geral',
          },
          {
            path: 'geral',
            component: GeneralSettingsPage,
            canActivate: [permissionGuard(['core:business:read'])],
          },
          {
            path: 'usuarios',
            component: UsersSettingsPage,
            canActivate: [permissionGuard(USERS_SETTINGS_PERMISSIONS)],
          },
          {
            path: 'equipes',
            component: TeamsSettingsPage,
            canActivate: [permissionGuard(TEAMS_SETTINGS_PERMISSIONS)],
          },
          {
            path: 'assinatura',
            component: SubscriptionSettingsPage,
            canActivate: [permissionGuard(['billing:subscription:read'])],
          },
          {
            path: 'assinatura/planos',
            loadComponent: () =>
              import('./features/settings/pages/subscription-plans.page').then((m) => m.SubscriptionPlansPage),
            canActivate: [permissionGuard(['billing:subscription:read'])],
          },
          {
            path: 'uso-e-quotas',
            loadComponent: () =>
              import('./features/billing/pages/uso-quotas.page').then((m) => m.UsoQuotasPage),
            canActivate: [permissionGuard(['billing:usage:read'])],
          },
          {
            path: 'cobranca',
            loadComponent: () =>
              import('./features/billing/pages/cobranca.page').then((m) => m.CobrancaPage),
            canActivate: [permissionGuard(['billing:wallet:read'])],
          },
          {
            path: 'permissoes',
            component: SecuritySettingsPage,
            canActivate: [permissionGuard(SECURITY_SETTINGS_PERMISSIONS)],
            data: { settingsSection: 'roles' },
          },
          {
            path: 'service-accounts',
            component: SecuritySettingsPage,
            canActivate: [permissionGuard(SECURITY_SETTINGS_PERMISSIONS)],
            data: { settingsSection: 'serviceAccounts' },
          },
          {
            path: 'seguranca',
            redirectTo: 'permissoes',
            pathMatch: 'full',
          },
          {
            path: 'perfil',
            component: ProfileSettingsPage,
          },
        ],
      },
      {
        path: 'crm',
        canActivate: [permissionGuard(['crm:read'])],
        loadChildren: () => import('./features/crm/crm.routes').then((m) => m.crmRoutes),
      },
      {
        path: 'atendimento',
        redirectTo: 'conversas/visao-geral',
        pathMatch: 'full',
      },
      {
        path: 'atendimento/configuracoes/labels',
        redirectTo: 'conversas/preferencias/labels',
        pathMatch: 'full',
      },
      {
        path: 'atendimento/configuracoes/sla',
        redirectTo: 'conversas/preferencias/sla',
        pathMatch: 'full',
      },
      {
        path: 'marketing',
        redirectTo: 'marketing/midia-paga',
        pathMatch: 'full',
      },
      {
        path: 'marketing/midia-paga',
        canActivate: [permissionGuard(['marketing:paid_media:read'])],
        loadComponent: () =>
          import('./features/marketing/pages/midia-paga.page').then((m) => m.MidiaPagaPage),
      },
      {
        path: 'marketing/analise',
        canActivate: [permissionGuard(['marketing:analysis:read'])],
        loadComponent: () =>
          import('./features/marketing/pages/analise.page').then((m) => m.AnalisePage),
      },
      {
        path: 'marketing/campanhas',
        canActivate: [permissionGuard(['marketing:campaigns:read'])],
        loadChildren: () =>
          import('./features/marketing/campanhas/campanhas.routes').then((m) => m.campanhasRoutes),
      },
      {
        path: 'marketing/analise-conteudo',
        canActivate: [permissionGuard(['marketing:content-analysis:read'])],
        loadComponent: () =>
          import('./features/marketing/pages/analise-conteudo.page').then((m) => m.AnaliseConteudoPage),
      },
      {
        path: 'crm',
        redirectTo: 'crm/contatos',
        pathMatch: 'full',
      },
      {
        path: 'crm/deals',
        redirectTo: 'crm/oportunidades',
        pathMatch: 'full',
      },
      {
        path: 'crm/pipeline',
        redirectTo: 'crm/oportunidades',
        pathMatch: 'full',
      },
      {
        path: 'receita',
        redirectTo: 'vendas',
        pathMatch: 'full',
      },
      {
        path: 'conversas',
        redirectTo: 'conversas/visao-geral',
        pathMatch: 'full',
      },
      {
        path: 'conversas/visao-geral',
        component: ConversationsPage,
        canActivate: [anyPermissionGuard(CONVERSATION_VISIBILITY_PERMISSIONS)],
        data: { conversationsPreset: 'overview', conversationsViewPath: 'visao-geral' },
      },
      {
        path: 'conversas/visao-geral/:conversationId',
        component: ConversationsPage,
        canActivate: [anyPermissionGuard(CONVERSATION_VISIBILITY_PERMISSIONS)],
        data: { conversationsPreset: 'overview', conversationsViewPath: 'visao-geral' },
      },
      {
        path: 'conversas/meus-atendimentos',
        component: ConversationsPage,
        canActivate: [anyPermissionGuard(CONVERSATION_VISIBILITY_PERMISSIONS)],
        data: { conversationsPreset: 'mine', conversationsViewPath: 'meus-atendimentos' },
      },
      {
        path: 'conversas/meus-atendimentos/:conversationId',
        component: ConversationsPage,
        canActivate: [anyPermissionGuard(CONVERSATION_VISIBILITY_PERMISSIONS)],
        data: { conversationsPreset: 'mine', conversationsViewPath: 'meus-atendimentos' },
      },
      {
        path: 'conversas/aguardando',
        component: ConversationsPage,
        canActivate: [anyPermissionGuard(CONVERSATION_VISIBILITY_PERMISSIONS)],
        data: { conversationsPreset: 'waiting', conversationsViewPath: 'aguardando' },
      },
      {
        path: 'conversas/aguardando/:conversationId',
        component: ConversationsPage,
        canActivate: [anyPermissionGuard(CONVERSATION_VISIBILITY_PERMISSIONS)],
        data: { conversationsPreset: 'waiting', conversationsViewPath: 'aguardando' },
      },
      {
        path: 'conversas/chamadas',
        canActivate: [anyPermissionGuard(CONVERSATION_VISIBILITY_PERMISSIONS)],
        loadComponent: () =>
          import('./features/conversations/telephony/chamadas.page').then((m) => m.ChamadasPage),
      },
      {
        path: 'conversas/analises',
        canActivate: [permissionGuard(['conversations:analysis:read'])],
        loadComponent: () =>
          import('./features/conversations/analysis/conversation-analysis.page').then(
            (m) => m.ConversationAnalysisPage,
          ),
      },
      {
        path: 'conversas/analises/:runId',
        canActivate: [permissionGuard(['conversations:analysis:read'])],
        loadComponent: () =>
          import('./features/conversations/analysis/conversation-analysis.page').then(
            (m) => m.ConversationAnalysisPage,
          ),
      },
      {
        path: 'conversas/preferencias',
        component: ConversationPreferencesPage,
        children: [
          {
            path: '',
            redirectTo: 'labels',
            pathMatch: 'full',
          },
          {
            path: 'labels',
            component: LabelsSettingsPage,
            canActivate: [permissionGuard(['conversations:base:labels:manage'])],
          },
          {
            path: 'sla',
            component: SlaSettingsPage,
            canActivate: [permissionGuard(['conversations:sla:admin'])],
          },
        ],
      },
      {
        path: 'agentes/automacoes',
        component: AutomationBuilderPage,
        canActivate: [permissionGuard(['agents:read'])],
      },
      {
        path: 'agentes/arquetipos',
        component: ArchetypesListPage,
        canActivate: [permissionGuard(['agents:read'])],
      },
      {
        path: 'agentes/arquetipos/novo',
        component: ArchetypeEditorPage,
        canActivate: [permissionGuard(['agents:write'])],
      },
      {
        path: 'agentes/arquetipos/:archetypeId',
        component: ArchetypeEditorPage,
        canActivate: [permissionGuard(['agents:read'])],
        canDeactivate: [archetypeEditorCanDeactivate],
      },
      {
        path: 'agentes/novo',
        component: AgentBuilderPage,
        canActivate: [permissionGuard(['agents:write'])],
        canDeactivate: [agentBuilderCanDeactivate],
        data: { agentsMode: 'create' },
      },
      {
        path: 'agentes/:agentId/editar',
        redirectTo: 'agentes/:agentId/configuracao',
      },
      {
        path: 'agentes/:agentId',
        component: AgentDetailPage,
        canActivate: [permissionGuard(['agents:read'])],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/agents/detail/tabs/agent-overview.tab').then((m) => m.AgentOverviewTabComponent),
          },
          {
            path: 'configuracao',
            component: AgentBuilderPage,
            canActivate: [permissionGuard(['agents:write'])],
            canDeactivate: [agentBuilderCanDeactivate],
            data: { agentsMode: 'edit', agentsEmbedded: true, agentTab: 'configuracao' },
          },
          {
            path: 'missao',
            component: AgentBuilderPage,
            canActivate: [permissionGuard(['agents:write'])],
            canDeactivate: [agentBuilderCanDeactivate],
            data: { agentsMode: 'edit', agentsEmbedded: true, agentTab: 'missao' },
          },
          {
            path: 'versoes',
            loadComponent: () =>
              import('./features/agents/detail/tabs/agent-versions.tab').then((m) => m.AgentVersionsTabComponent),
          },
          {
            path: 'execucoes',
            loadComponent: () =>
              import('./features/agents/detail/tabs/agent-executions.tab').then((m) => m.AgentExecutionsTabComponent),
          },
        ],
      },
      ...PLACEHOLDER_ROUTE_MODULES.map(createPlaceholderRoute),
      {
        path: 'atendimento/:conversationId',
        redirectTo: 'conversas/visao-geral/:conversationId',
      },
      {
        path: 'biblioteca',
        component: KnowledgePage,
        canActivate: [permissionGuard(['knowledge:bases:read'])],
      },
      {
        path: 'agentes',
        component: AgentsListPage,
        canActivate: [permissionGuard(['agents:read'])],
        data: { agentsMode: 'list' },
      },
      {
        path: 'conhecimento',
        redirectTo: 'biblioteca',
        pathMatch: 'full',
      },
      {
        path: 'produtos',
        component: RevenueCatalogPage,
        canActivate: [permissionGuard(['revenue:products:read'])],
      },
      {
        path: 'produtos/importar',
        component: ImportWizardPage,
        data: { kind: 'products' },
      },
      {
        path: 'produtos/:id',
        component: ProdutoDetalhePage,
        canActivate: [permissionGuard(['revenue:products:read'])],
      },
      {
        path: 'vendas',
        component: VendasListaPage,
        canActivate: [permissionGuard(['revenue:sales:read'])],
      },
      {
        path: 'vendas/criar',
        component: SalesCriarPage,
      },
      {
        path: 'vendas/:id/editar',
        component: SalesEditarPage,
      },
      {
        path: 'vendas/importar',
        component: ImportWizardPage,
        data: { kind: 'sales' },
      },
      {
        path: 'leads',
        component: LeadsListaPage,
      },
      {
        path: 'integracoes',
        canActivate: [permissionGuard(['integrations:connections:read'])],
        loadChildren: () =>
          import('./features/integracoes/integracoes.routes').then((m) => m.INTEGRACOES_ROUTES),
      },
      {
        path: 'forbidden',
        component: ForbiddenPage,
      },
    ],
  },
];

function createPlaceholderRoute(module: AppModuleNavItem): Route {
  const permissions = module.permissions ?? [];
  const canActivate =
    permissions.length > 0
      ? [module.permissionMode === 'any' ? anyPermissionGuard(permissions) : permissionGuard(permissions)]
      : undefined;

  return {
    path: module.path.slice(1),
    component: ModulePlaceholderPage,
    data: { module },
    ...(canActivate ? { canActivate } : {}),
  };
}
